import { useAutoAnimate } from "@msuite/ui/libs/auto-animate";
import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import type { ChatItem } from "./shared";
import { InsightsSpotlightAnswer } from "./insights-spotlight-answer";
import { InsightsSpotlightQuestion } from "./insights-spotlight-question";
import { InsightsSpotlightThinking } from "./insights-spotlight-thinking";
import type { Disclosure } from "@msuite/ui/hooks";

/** Props Interface */
interface InsightsSpotlightChatProps {
	items: ChatItem[];
	onClose: Disclosure["onClose"];
}

export function InsightsSpotlightChat({ items, onClose }: InsightsSpotlightChatProps) {
	/** Hooks */
	const [parent] = useAutoAnimate({ disrespectUserMotionPreference: true });

	/** State */
	const containerRef = useRef<HTMLDivElement>(null);

	/** Effects */
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		container.scrollTop = container.scrollHeight;
	}, [items]);

	/** Functions */
	function setRefs(node: HTMLDivElement | null) {
		containerRef.current = node;
		parent(node);
	}

	/** Render */
	return (
		<motion.div
			ref={setRefs}
			className="top-0 h-[85vh] absolute w-1/2 flex flex-col pb-12 gap-4 overflow-y-auto scrollbar-hidden max-w-[700px]"
			initial={{ scale: 0.9, opacity: 0 }}
			animate={{ scale: 1, opacity: 1 }}
			exit={{ scale: 0.8, opacity: 0 }}
		>
			<div
				className="absolute inset-0"
				onClick={onClose}
			/>
			<div className="grow" />
			{items.map((item) =>
				item.type === "answer" ? (
					<InsightsSpotlightAnswer
						item={item.item}
						key={item.id}
					/>
				) : item.type === "question" ? (
					<InsightsSpotlightQuestion
						item={item.item}
						key={item.id}
					/>
				) : (
					<InsightsSpotlightThinking key={item.id} />
				),
			)}
		</motion.div>
	);
}
